import { QueryClient, notifyManager } from '@tanstack/react-query'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { contextoSesion } from '../auth/SesionProvider'
import { crearClienteApi, type ClienteApi } from './cliente'
import { ErrorApi } from './errores'

/**
 * Punto unico donde el arbol de React obtiene el cliente de API. El cliente se
 * construye a partir de `obtenerToken` de la sesion, asi que ningun componente
 * necesita saber nada de como se guarda el token para pedir datos.
 */
const contextoApi = createContext<ClienteApi | null>(null)

interface Props {
  children: ReactNode
  /** Doble para las pruebas: evita montar la sesion para probar una consulta. */
  cliente?: ClienteApi
}

export function ApiProvider({ children, cliente }: Props) {
  // Se lee el contexto directamente en vez de `useSesion()` porque con un
  // cliente inyectado no hace falta sesion y `useSesion()` lanzaria.
  const sesion = useContext(contextoSesion)

  const valor = useMemo(() => {
    if (cliente) return cliente
    if (!sesion) {
      throw new Error('ApiProvider necesita un SesionProvider o un cliente inyectado')
    }
    // Un 401 con token cierra la sesion: RutaProtegida devuelve al login.
    return crearClienteApi(sesion.obtenerToken, () => void sesion.salir())
  }, [cliente, sesion])

  return <contextoApi.Provider value={valor}>{children}</contextoApi.Provider>
}

export function useApi(): ClienteApi {
  const valor = useContext(contextoApi)
  if (!valor) throw new Error('useApi debe usarse dentro de ApiProvider')
  return valor
}

/**
 * Fabrica del QueryClient de la aplicacion. Vive aqui, junto al cliente de API,
 * porque la politica de reintentos depende de `ErrorApi`: reintentar un 401 o un
 * 404 no arregla nada y solo retrasa el mensaje al usuario. Las pruebas usan
 * esta misma fabrica para no comprobar una copia divergente de la politica.
 */
export function crearClienteConsultas(): QueryClient {
  // TanStack Query avisa a los componentes desde un `setTimeout(cb, 0)`, o sea
  // un turno de macrotarea despues de escribir la respuesta en la cache. En el
  // sondeo de una conversacion eso deja una ventana en la que la cache ya tiene
  // los mensajes nuevos y la pantalla todavia no; se ve al medirlo con el reloj
  // detenido. Con una microtarea el aviso viaja en el mismo turno en el que
  // llega la respuesta, que es lo que hacia la propia libreria antes de mover el
  // aviso al temporizador, y sigue estando agrupado por `notifyManager`.
  notifyManager.setScheduler((avisar) => queueMicrotask(avisar))

  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (intentos, error) => {
          if (error instanceof ErrorApi && (error.estado < 500 || error.esNoDisponible || error.esSinConfigurar)) return false
          return intentos < 2
        },
      },
    },
  })
}
